/**
 * Chat IndexedDB Persistence
 * 
 * Stores chat messages locally for:
 * - Offline access
 * - Fast loading
 * - Exact-once delivery guarantee on reconnect
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { ChatMessage, ChatThread } from '@/store/chat';
import { logger } from './logger';

interface ChatDB extends DBSchema {
  threads: {
    key: string;
    value: ChatThread;
    indexes: { 'by-order': string };
  };
  messages: {
    key: string;
    value: ChatMessage;
    indexes: { 'by-thread': string; 'by-timestamp': string };
  };
  outbox: {
    key: string;
    value: {
      id: string;
      threadId: string;
      message: string;
      timestamp: string;
      retryCount: number;
    };
  };
}

class ChatDatabase {
  private db: IDBPDatabase<ChatDB> | null = null;

  async init(): Promise<void> {
    try {
      this.db = await openDB<ChatDB>('rider-chat-db', 1, {
        upgrade(db) {
          // Threads store
          const threadsStore = db.createObjectStore('threads', { keyPath: 'id' });
          threadsStore.createIndex('by-order', 'orderId');

          // Messages store
          const messagesStore = db.createObjectStore('messages', { keyPath: 'id' });
          messagesStore.createIndex('by-thread', 'threadId');
          messagesStore.createIndex('by-timestamp', 'timestamp');

          // Outbox for offline messages
          db.createObjectStore('outbox', { keyPath: 'id' });
        },
      });

      logger.info('Chat database initialized');
    } catch (error) {
      logger.error('Failed to initialize chat database', error as Error);
    }
  }

  // Threads
  async saveThread(thread: ChatThread): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.put('threads', thread);
  }

  async getThread(threadId: string): Promise<ChatThread | undefined> {
    if (!this.db) await this.init();
    return await this.db!.get('threads', threadId);
  }

  async getAllThreads(): Promise<ChatThread[]> {
    if (!this.db) await this.init();
    return await this.db!.getAll('threads');
  }

  async deleteThread(threadId: string): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.delete('threads', threadId);
  }

  // Messages
  async saveMessage(message: ChatMessage): Promise<void> {
    if (!this.db) await this.init();
    
    // Check for duplicates before saving
    const existing = await this.db!.get('messages', message.id);
    if (existing) {
      logger.warn('Duplicate message prevented in IndexedDB', { messageId: message.id });
      return;
    }

    await this.db!.put('messages', message);
  }

  async getMessage(messageId: string): Promise<ChatMessage | undefined> {
    if (!this.db) await this.init();
    return await this.db!.get('messages', messageId);
  }

  async getMessagesByThread(threadId: string): Promise<ChatMessage[]> {
    if (!this.db) await this.init();
    return await this.db!.getAllFromIndex('messages', 'by-thread', threadId);
  }

  async deleteMessage(messageId: string): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.delete('messages', messageId);
  }

  // Outbox (offline messages)
  async addToOutbox(item: {
    id: string;
    threadId: string;
    message: string;
    timestamp: string;
    retryCount: number;
  }): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.put('outbox', item);
  }

  async getOutboxItems(): Promise<Array<{
    id: string;
    threadId: string;
    message: string;
    timestamp: string;
    retryCount: number;
  }>> {
    if (!this.db) await this.init();
    return await this.db!.getAll('outbox');
  }

  async removeFromOutbox(id: string): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.delete('outbox', id);
  }

  async updateOutboxRetryCount(id: string, retryCount: number): Promise<void> {
    if (!this.db) await this.init();
    const item = await this.db!.get('outbox', id);
    if (item) {
      item.retryCount = retryCount;
      await this.db!.put('outbox', item);
    }
  }

  // Cleanup
  async clearAll(): Promise<void> {
    if (!this.db) await this.init();
    
    const tx = this.db!.transaction(['threads', 'messages', 'outbox'], 'readwrite');
    await Promise.all([
      tx.objectStore('threads').clear(),
      tx.objectStore('messages').clear(),
      tx.objectStore('outbox').clear(),
    ]);
    
    await tx.done;
    logger.info('Chat database cleared');
  }
}

export const chatDb = new ChatDatabase();


















